import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatCentsUSD } from "@/lib/utils/currency.utils";
import type { AutopayPreview, RecipientAutopayState } from "@/services/vendor-autopay.service";
import { enableRecipientAutopay, getRecipientAutopayPreview } from "../payment-dues.api";

interface AutopayConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientType: "individual" | "subs";
  recipientId: string;
  recipientName: string;
  paymentTermsDays: number;
  payerMethodSummary: string;
  onEnabled: (state: RecipientAutopayState) => void;
}

export default function AutopayConsentDialog({
  open,
  onOpenChange,
  recipientType,
  recipientId,
  recipientName,
  paymentTermsDays,
  payerMethodSummary,
  onEnabled,
}: AutopayConsentDialogProps) {
  const [preview, setPreview] = useState<AutopayPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      setPreview(await getRecipientAutopayPreview(recipientType, recipientId));
    } catch (err: any) {
      setPreviewError(err?.message ?? "Failed to load the past-due amount.");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setConsentChecked(false);
    setSubmitError(null);
    void loadPreview();
  }, [open, recipientType, recipientId]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const state = await enableRecipientAutopay(recipientType, recipientId);
      toast.success(`Autopay enabled for ${recipientName}.`);
      onEnabled(state);
      onOpenChange(false);
    } catch (err: any) {
      setSubmitError(err?.message ?? "Failed to enable autopay.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="z-[70] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Turn on autopay for {recipientName}?</DialogTitle>
          <DialogDescription>
            Dues are charged automatically once they pass Net {paymentTermsDays} terms, as one combined payment per day.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {previewLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Calculating past-due payments…</span>
            </div>
          )}

          {previewError && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm space-y-2">
              <p>{previewError}</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void loadPreview()}>
                Retry
              </Button>
            </div>
          )}

          {preview && !previewLoading && (
            <div className="rounded-md border p-3 text-sm" data-testid="autopay-preview">
              {preview.amountCents > 0 ? (
                <p>
                  By turning this on you will pay{" "}
                  <span className="font-semibold">{formatCentsUSD(preview.amountCents)}</span> on the next daily run for{" "}
                  {preview.itemCount} payment{preview.itemCount === 1 ? "" : "s"} already past Net {preview.termsDays}{" "}
                  terms.
                </p>
              ) : (
                <p>
                  There are no past-term dues right now. Autopay will begin charging as dues pass Net{" "}
                  {preview.termsDays} terms.
                </p>
              )}
            </div>
          )}

          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="autopay-consent"
              data-testid="autopay-consent-checkbox"
              checked={consentChecked}
              disabled={submitting || previewLoading || Boolean(previewError)}
              onCheckedChange={(value) => setConsentChecked(value === true)}
              className="mt-0.5"
            />
            <label htmlFor="autopay-consent" className="text-sm leading-5 text-foreground">
              I authorize Shyne to automatically charge my saved payment method ({payerMethodSummary}) daily for
              payments owed to {recipientName} once they pass the agreed Net terms, until I turn autopay off.
            </label>
          </div>

          {submitError && <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">{submitError}</div>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="autopay-confirm"
            disabled={!consentChecked || submitting || previewLoading || Boolean(previewError)}
            onClick={() => void handleConfirm()}
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            {submitting ? "Enabling…" : "Turn on autopay"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
